/**
 * Mapa de rutas. Las doce vistas de la v1.0 congelada (ADR-007), con las rutas de negocio
 * que fijó la orden de Fase 1. `id` es el identificador de la v1.0; `path` es la URL nueva.
 *
 * ⛔ No se agregan rutas sin módulo detrás: «Proyectos», «Entregables» y «Nuevo pedido»
 * siguen sin existir porque no hay módulo que las sostenga.
 *
 * 🔑 ARQUITECTURA DE NAVEGACIÓN DE LA v1.0 — restituida.
 *
 * La v1.0 no pone las doce vistas en la barra: pone SEIS (`nav: true`) y lleva las otras
 * seis al riel derecho de Quick Actions (`rail`), cada una con su icono y su color. Las
 * doce quedan alcanzables, pero la barra superior sólo carga las primarias.
 *
 * Durante la auditoría se expandió la nav a doce ítems creyendo corregir un defecto de
 * visibilidad. Era un malentendido: no faltaban tabs, sobraban. Esto restituye el diseño
 * original — que además vuelve innecesario aquel arreglo.
 */
import type { IconName } from '../components/Icon.tsx';

export interface RouteDef {
  readonly id: string;
  readonly path: string;
  readonly label: string;
  readonly eyebrow: string;
  readonly title: string;
  /** Aparece en la barra superior. */
  readonly nav?: boolean;
  /** Aparece en el riel de Quick Actions, con este icono y color. */
  readonly rail?: { readonly icon: IconName; readonly bg: string; readonly color: string };
  readonly badge?: string;
  /** Grupo del sidebar (diseño de Martín). `null` = grupo de cabecera, sin título. */
  readonly grupo?: string | null;
  /** Icono en el sidebar. */
  readonly icono?: IconName;
}

export const ROUTES: readonly RouteDef[] = [
  // ── ORDEN OFICIAL (Sprint final UI/UX) ──────────────────────────────────
  // Definido por Dirección. Sigue el flujo real —Dashboard, después el
  // recorrido del pedido, después referencia y sistema— y reemplaza cualquier
  // orden anterior. Cada sección lleva SU icono, no uno genérico repetido.
  { id: 'dashboard',     path: '/dashboard',       label: 'Dashboard',         eyebrow: 'PANEL',                  title: 'Centro Operativo',   nav: true, grupo: null, icono: 'panel' },
  { id: 'brief',         path: '/brief',           label: 'Nuevo Brief',       eyebrow: '1 · EMPEZÁS ACÁ',        title: '¿Qué necesitás?',    nav: true, grupo: 'Flujo creativo', icono: 'brief' },
  { id: 'aprobaciones',  path: '/approvals',       label: 'Aprobaciones',      eyebrow: '2 · TU DECISIÓN',        title: 'Aprobaciones',       nav: true, grupo: 'Flujo creativo', icono: 'shield-check' },
  { id: 'pipeline',      path: '/pipeline',        label: 'Creative Pipeline', eyebrow: '3 · TRABAJO EN CURSO',   title: 'Creative Pipeline',  nav: true, grupo: 'Flujo creativo', icono: 'workflow' },
  { id: 'biblioteca',    path: '/library',         label: 'Entregables',       eyebrow: '4 · TUS PIEZAS',         title: 'Entregables',        nav: true, grupo: 'Flujo creativo', icono: 'package' },

  { id: 'playground',    path: '/playground',      label: 'Playground',        eyebrow: 'SIMULADOR',              title: 'Playground',         nav: true, grupo: 'Explorar', icono: 'flask',
    rail: { icon: 'eye',            bg: 'rgba(139,92,246,0.14)',  color: '#a78bfa' } },
  { id: 'decision',      path: '/decision-engine', label: 'Decision Engine',   eyebrow: 'CÓMO DECIDE EL SISTEMA', title: 'Decision Engine',    nav: true, grupo: 'Explorar', icono: 'cpu',
    rail: { icon: 'shield-check',   bg: 'rgba(16,185,129,0.14)',  color: '#10b981' } },
  { id: 'arquitectura',  path: '/architecture',    label: 'Arquitectura',      eyebrow: 'CÓMO ESTÁ CONSTRUIDO',   title: 'Arquitectura',       nav: true, grupo: 'Explorar', icono: 'layers',
    rail: { icon: 'layout-grid',    bg: 'rgba(6,182,212,0.14)',   color: '#06b6d4' } },
  { id: 'integraciones', path: '/integrations',    label: 'Integraciones',     eyebrow: 'ESTADO VERIFICADO',      title: 'Integraciones',      nav: true, grupo: 'Explorar', icono: 'plug',
    rail: { icon: 'refresh-cw',     bg: 'rgba(139,92,246,0.14)',  color: '#c4b5fd' } },

  { id: 'manual',        path: '/manual',          label: 'Manual',            eyebrow: 'DOCUMENTACIÓN',          title: 'Manual',             nav: true, grupo: 'Ayuda', icono: 'book',
    rail: { icon: 'clipboard-list', bg: 'rgba(245,158,11,0.14)',  color: '#f59e0b' } },
  { id: 'casos',         path: '/use-cases',       label: 'Casos de uso',      eyebrow: 'EJEMPLOS REALES',        title: 'Casos de uso',       nav: true, grupo: 'Ayuda', icono: 'folders' },
  { id: 'faq',           path: '/faq',             label: 'FAQ',               eyebrow: 'PREGUNTAS FRECUENTES',   title: 'FAQ',                nav: true, grupo: 'Ayuda', icono: 'help-circle',
    rail: { icon: 'contact',        bg: 'rgba(148,163,184,0.14)', color: '#94a3b8' } },
];

export const NAV_ROUTES  = ROUTES.filter((r) => r.nav);
export const RAIL_ROUTES = ROUTES.filter((r) => r.rail);

export const DEFAULT_ROUTE = ROUTES[0]!;

export function routeForPath(path: string): RouteDef | null {
  const clean = path.replace(/\/+$/, '') || DEFAULT_ROUTE.path;
  return ROUTES.find((r) => r.path === clean) ?? null;
}
